import type { SharedContentType } from '../types/api'

export interface DetectedSharedUrl {
  type: SharedContentType
  url: string
  key: string
}

/** Cards per message are capped to bound fetch fan-out; extra URLs stay plain links. */
export const MAX_SHARED_CARDS = 3

const URL_TOKEN_REGEX = /https?:\/\/[^\s<>"']+/gi

const PATH_PATTERNS: Array<{ type: SharedContentType; pattern: RegExp }> = [
  { type: 'post', pattern: /^\/post\/([^/]+)\/?$/ },
  { type: 'property', pattern: /^\/property\/([^/]+)\/?$/ },
]

const getAllowedHosts = (): Set<string> => {
  const hosts = new Set<string>()
  const websiteUrl = import.meta.env.VITE_WEBSITE_URL as string | undefined
  if (websiteUrl) {
    try {
      hosts.add(new URL(websiteUrl).host)
    } catch {
      // Misconfigured env var — fall through to the embedding origin.
    }
  }
  if (typeof window !== 'undefined' && window.location?.host) {
    hosts.add(window.location.host)
  }
  return hosts
}

/**
 * Find platform Post/Property URLs in a chat message. A URL qualifies when its
 * host is the platform website (VITE_WEBSITE_URL, or the embedding origin) and
 * its path is /post/<slug> or /property/<slug>. The lookup key is the slug's
 * last dash-segment — the same rule the website's own pages use to resolve it.
 * Deduped by URL, capped at MAX_SHARED_CARDS.
 */
export const detectSharedContentUrls = (text: string): DetectedSharedUrl[] => {
  if (!text) return []
  const allowedHosts = getAllowedHosts()
  if (allowedHosts.size === 0) return []

  const results: DetectedSharedUrl[] = []
  const seen = new Set<string>()

  for (const token of text.match(URL_TOKEN_REGEX) ?? []) {
    if (results.length >= MAX_SHARED_CARDS) break
    // Strip trailing punctuation that commonly rides along in prose.
    const cleaned = token.replace(/[).,!?]+$/, '')
    let parsed: URL
    try {
      parsed = new URL(cleaned)
    } catch {
      continue
    }
    if (!allowedHosts.has(parsed.host)) continue

    for (const { type, pattern } of PATH_PATTERNS) {
      const match = parsed.pathname.match(pattern)
      if (!match) continue
      const slug = decodeURIComponent(match[1])
      const key = slug.split('-').pop() ?? ''
      if (!key || seen.has(cleaned)) break
      seen.add(cleaned)
      results.push({ type, url: cleaned, key })
      break
    }
  }

  return results
}
